import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Workflow } from '@digital-twins/design-system/icons'

import { WorkflowTemplateSummary } from '@digital-twins/shared-types'

import {
  ComposerAppearance,
  composerPanelClassName,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  composerTriggerClassName,
} from '@/components/composer/composerAppearance'
import { templateSurface } from '@/lib/workflows/templateSurface'
import { cn } from '@/lib/utils'

/**
 * The composer's workflow entry point (decision D3,
 * docs/plans/assistant-platform-v1-execution-plan.md): a `Workflows` dropdown
 * listing the registry, built for MANY workflows rather than the one flagship
 * template seeded today. Picking a row arms it; `HomeLabStage` renders the
 * armed choice as its own header chip (`ActiveWorkflowChip`), the same split
 * `StudyTypeControls` / `ActiveStudyTypeChip` already use (door in the
 * footer, answer above the textarea).
 *
 * Click-outside-to-close is copied from `StudyTypeControls`, the composer's
 * own existing dropdown, rather than the design-system `DropdownMenu`: this
 * keeps every footer control behaving the same way as its neighbours.
 */
interface WorkflowMenuProps {
  templates: Array<WorkflowTemplateSummary>
  selectedSlug: string | null
  onSelect: (slug: string) => void
  /**
   * Where the panel opens relative to the trigger. `below` is the home
   * composer's behaviour, where the composer sits mid-page. The thread mount
   * passes `above`: its control row is pinned to the bottom of the viewport,
   * where a menu dropping down would open off-screen, the same reason
   * `EffortToggle` and `ThreadCommandMenu` open upward.
   */
  placement?: 'below' | 'above'
  /**
   * Which edge the 280px panel hangs from. `start` matches the home mount;
   * the thread mount passes `end` because its trigger sits in the row's
   * right-hand `ml-auto` cluster, where a start-anchored panel overflows the
   * column.
   */
  align?: 'start' | 'end'
  disabled?: boolean
  /** Surfaces as the trigger's `title`, mirroring `ThreadCommandMenu`. */
  disabledReason?: string
  appearance?: ComposerAppearance
  className?: string
}

export function WorkflowMenu(props: WorkflowMenuProps) {
  const {
    templates,
    selectedSlug,
    onSelect,
    placement = 'below',
    align = 'start',
    disabled = false,
    disabledReason,
    appearance = 'external',
    className,
  } = props
  const { t } = useTranslation('app')

  const lobbyTemplates = templates.filter(
    (template) => templateSurface(template) === 'lobby',
  )

  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  /* Force the panel shut the moment the door goes disabled: a run started
     from the gate while this panel was open would otherwise leave it sitting
     over a dead door. */
  useEffect(() => {
    if (disabled) {
      setIsOpen(false)
    }
  }, [disabled])

  /* Nothing seeded, nothing to open. The lobby's own empty state
     ("Fairgen builds workflows for your team") lives on `/workflows`; the
     composer just omits the door rather than repeating that copy here. */
  if (lobbyTemplates.length === 0) {
    return null
  }

  const select = (slug: string) => {
    onSelect(slug)
    setIsOpen(false)
  }

  return (
    <div ref={rootRef} className={cn('relative flex shrink-0 items-center', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={t('homeLab.workflowMenuTrigger')}
        disabled={disabled}
        title={disabledReason}
        className={composerTriggerClassName(appearance, {
          isOpen,
          /* Only a slug this menu can actually pick. A thread-action (`/test`)
             shares the page's armed-slug state but is not a lobby workflow. */
          isSelected: Boolean(
            selectedSlug &&
              lobbyTemplates.some((template) => template.slug === selectedSlug),
          ),
        })}
        data-testid="home-lab-workflow-trigger"
      >
        <Workflow className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className={composerTriggerCaptionClassName(appearance)}>
          {t('homeLab.workflowMenuTrigger')}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200',
            composerTriggerChevronClassName(appearance),
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          role="menu"
          className={cn(
            'home-lab-mega-enter absolute z-50 rounded-2xl p-1.5',
            /* `above` opens UPWARD: the thread's control row sits at the
               bottom of the viewport, where a menu dropping down would open
               off-screen. */
            placement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2',
            align === 'end' ? 'right-0' : 'left-0',
            'w-[280px] max-w-[calc(100vw-3rem)]',
            composerPanelClassName(appearance),
          )}
          data-testid="home-lab-workflow-menu"
        >
          {lobbyTemplates.map((template) => (
            <button
              key={template.token}
              type="button"
              role="menuitemradio"
              aria-checked={template.slug === selectedSlug}
              onClick={() => select(template.slug)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-xl px-3 py-2 text-left',
                appearance === 'internal'
                  ? 'transition-colors duration-150 hover:bg-[var(--home-pill-track-bg)]'
                  : 'transition-colors hover:bg-muted/70',
              )}
              data-testid={`home-lab-workflow-option-${template.slug}`}
            >
              <span
                className={cn(
                  'truncate font-medium',
                  appearance === 'internal'
                    ? 'text-[13px] text-[var(--home-composer-fg)]'
                    : 'text-xs text-foreground',
                )}
              >
                {template.displayName}
              </span>
              <span
                className={cn(
                  'line-clamp-1',
                  appearance === 'internal'
                    ? 'text-[12px] text-[var(--home-pill-inactive-fg)]'
                    : 'text-xs text-muted-foreground',
                )}
              >
                {template.description}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
